import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Activity as ActivityIcon, Workflow, Bot, CheckSquare, Bell, MessageSquare, Clock } from 'lucide-react';
import { haptic } from '../../telegram';
import { pipelinesApi } from '../../api/pipelinesApi';
import { agentsApi } from '../../api/agentsApi';
import { tasksApi } from '../../api/tasksApi';
import { remindersApi } from '../../api/remindersApi';
import { listConversations } from '../../api/chatApi';

type Kind = 'pipe' | 'agent' | 'task' | 'reminder' | 'chat';
interface Item { id: string; kind: Kind; title: string; sub: string; ts: number; to: string; }

function rel(ts: number): string {
  if (!ts) return '';
  const m = Math.round((Date.now() - ts) / 60000);
  if (m < 1) return 'только что';
  if (m < 60) return m + ' мин назад';
  const h = Math.round(m / 60);
  if (h < 24) return h + ' ч назад';
  const d = Math.round(h / 24);
  if (d < 7) return d + ' дн назад';
  return new Date(ts).toLocaleDateString('ru-RU');
}
const t = (s?: string | null) => (s ? new Date(s).getTime() : 0);

export function Activity() {
  const nav = useNavigate();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([pipelinesApi.list(), agentsApi.list(), tasksApi.list(), remindersApi.list(), listConversations(false)])
      .then(([p, a, tk, r, c]) => {
        const out: Item[] = [];
        if (p.status === 'fulfilled') (p.value || []).forEach((x: any) => out.push({ id: 'p' + x.id, kind: 'pipe', title: x.name || 'Сценарий', sub: x.last_fired_at ? 'Сценарий запускался' : 'Сценарий создан', ts: Math.max(t(x.last_fired_at), t(x.updated_at), t(x.created_at)), to: '/automation/' + x.id }));
        if (a.status === 'fulfilled') (a.value || []).forEach((x: any) => out.push({ id: 'a' + x.id, kind: 'agent', title: x.name, sub: 'Агент в команде', ts: t(x.created_at), to: '/settings/agents' }));
        if (tk.status === 'fulfilled') (tk.value || []).forEach((x: any) => out.push({ id: 't' + x.id, kind: 'task', title: x.title, sub: 'Задача запланирована', ts: Math.max(t(x.createdAt), t(x.dueAt)), to: '/settings/tasks' }));
        if (r.status === 'fulfilled') (r.value || []).forEach((x: any) => out.push({ id: 'r' + x.id, kind: 'reminder', title: x.text, sub: 'Напоминание', ts: Math.max(t(x.createdAt), t(x.dueAt)), to: '/settings/reminders' }));
        if (c.status === 'fulfilled') (c.value || []).forEach((x: any) => out.push({ id: 'c' + x.id, kind: 'chat', title: x.title || 'Диалог', sub: 'Диалог с ассистентом', ts: Math.max(t(x.updatedAt), t(x.createdAt)), to: '/assistant' }));
        out.sort((x, y) => y.ts - x.ts);
        setItems(out.slice(0, 30));
        setLoading(false);
      });
  }, []);

  const icon = (k: Kind) => k === 'pipe' ? <Workflow /> : k === 'agent' ? <Bot /> : k === 'task' ? <CheckSquare /> : k === 'reminder' ? <Bell /> : <MessageSquare />;

  return (
    <div className="rf-detail">
      <div className="rf-topbar">
        <button className="rf-back" onClick={() => { haptic('light'); nav(-1 as any); }}><ArrowLeft />Назад</button>
        <div className="rf-topbar-title">Последние действия</div>
      </div>
      <div className="rf-detail-scroll">
        <div className="rf-info"><ActivityIcon /><div><b>Лента активности</b><br />Последние действия в вашем пространстве: созданные сценарии и агенты, задачи, напоминания и диалоги.</div></div>
        {loading ? (
          <div className="rf-loading"><div className="rf-spin" /><p>Загрузка…</p></div>
        ) : items.length === 0 ? (
          <div className="rf-empty"><div className="rf-empty-ic"><ActivityIcon /></div><h3>Пока пусто</h3><p>Здесь появятся ваши последние действия</p></div>
        ) : (
          <div className="rf-rows rf-stagger">
            {items.map((it) => (
              <div key={it.id} className="rf-card rf-conn rf-click" onClick={() => { haptic('light'); nav(it.to); }}>
                <div className="rf-conn-row">
                  <div className="rf-row-ic">{icon(it.kind)}</div>
                  <div className="rf-conn-info"><div className="rf-conn-name">{it.title}</div><div className="rf-conn-sub">{it.sub}</div></div>
                  <span className="rf-time"><Clock />{rel(it.ts)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
